/** Write an extracted chat table as a real .xlsx.
 *
 *  "Real" is the requirement: a finance user opens this file, clicks a numeric
 *  column and expects a sum in the status bar. That is why the export is a
 *  workbook rather than CSV, and it is what shapes every decision below — typed
 *  cells with number formats, data starting clean at A1, and provenance kept off
 *  the data sheet where it would break "Format as Table", autofilter detection
 *  and pasting the range into a pivot.
 *
 *  Spec: docs/superpowers/specs/2026-09-22-chat-table-exports-design.md,
 *  "Cell coercion (xlsx)" and "Provenance" under Cross-cutting contracts.
 */

import { coerceCell, type CoercedCell, type TableCell } from "./coerce";
import { buildProvenance, type ProvenanceInput } from "./provenance";

import type { Cell, Font, Workbook, Worksheet } from "exceljs";

export const XLSX_MIME =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

const DATA_SHEET_NAME = "Table";
/** Named by the spec — do not rename; it is what the caveat is filed under. */
const SOURCE_SHEET_NAME = "Source";

/** What the workbook is written from. `header`/`rows` are `extractTable`'s
 *  output; the type is spelled out here rather than imported because `lib` must
 *  not depend on `components`. */
export interface XlsxTableInput {
  header: TableCell[];
  rows: TableCell[][];
  /** Everything `buildProvenance` needs except `rowCount`, which is derived from
   *  `rows` — the count is a claim about THIS table, so the writer that knows
   *  exactly how many rows it wrote owns it and no caller can let it drift. */
  provenance: Omit<ProvenanceInput, "rowCount">;
}

/** Schemes we will make clickable inside a downloaded workbook.
 *
 *  SECURITY, and the reason this is an allowlist rather than a denylist: `href`
 *  arrives verbatim from the hast. react-markdown's `urlTransform` sanitises the
 *  rendered `<a href>` but does NOT rewrite `node.properties.href`, so
 *  `javascript:`, `data:` and `file:` URLs reach this module unfiltered. A
 *  `file://host/share` UNC path in an .xlsx hyperlink is a real credential-leak
 *  vector on Windows — one click and SMB offers the user's NTLM hash to the
 *  host. This is a file we invite people to open and forward on, so anything not
 *  on this list is written as plain text: the label is still visible, the URL
 *  simply is not armed. Never emit an unrecognised scheme silently. */
const SAFE_LINK_SCHEMES = new Set(["https:", "http:", "mailto:"]);

const HEADER_FONT: Partial<Font> = {
  bold: true,
  color: { argb: "FFFFFFFF" },
};

const HEADER_FILL = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FF1F2937" },
} as const;

const LINK_FONT: Partial<Font> = {
  color: { argb: "FF1155CC" },
  underline: true,
};

const MIN_COLUMN_WIDTH = 10;
const MAX_COLUMN_WIDTH = 60;

/** exceljs ships CommonJS. Vite's dependency pre-bundle re-exports it with named
 *  exports, while a plain Node ESM loader (vitest, with the dep externalised)
 *  only detects `default`. Accept whichever arrives instead of depending on who
 *  loaded us. */
async function loadExcelJs(): Promise<typeof import("exceljs")> {
  const imported = (await import("exceljs")) as typeof import("exceljs") & {
    default?: typeof import("exceljs");
  };
  return imported.default ?? imported;
}

/** The URL to arm on a cell, or `undefined` to leave the cell as plain text.
 *
 *  Returns the parsed `href` rather than the input so control characters and odd
 *  spellings (`JavaScript:`, embedded newlines) cannot survive the check. A
 *  relative URL fails `new URL` and is dropped deliberately: inside a downloaded
 *  workbook it would resolve against the user's Downloads folder, which is
 *  useless at best. */
function safeHyperlink(href: string | undefined): string | undefined {
  if (!href) return undefined;
  let parsed: URL;
  try {
    parsed = new URL(href);
  } catch {
    return undefined;
  }
  return SAFE_LINK_SCHEMES.has(parsed.protocol) ? parsed.href : undefined;
}

/** Write one coerced value into a cell.
 *
 *  A hyperlink is applied only to a non-empty TEXT value. exceljs models a
 *  hyperlink as a cell value of `{ text, hyperlink }`, so arming a number would
 *  turn it back into a string and cost the column its sum — the one thing this
 *  export exists to provide. Numbers and dates therefore keep their type and
 *  lose the link; their label, which is what the reader sees, is unchanged. */
function writeValue(cell: Cell, coerced: CoercedCell): void {
  const link =
    typeof coerced.v === "string" && coerced.v !== ""
      ? safeHyperlink(coerced.link)
      : undefined;

  if (link !== undefined) {
    cell.value = { text: coerced.v as string, hyperlink: link };
    cell.font = LINK_FONT;
  } else if (coerced.v !== "") {
    cell.value = coerced.v;
  }
  // An empty value is left as a genuinely blank cell rather than an empty
  // string: blanks are skipped by COUNTA and by Excel's own type inference,
  // where a zero-length string is counted as data.

  if (coerced.fmt !== undefined) cell.numFmt = coerced.fmt;
}

/** Header labels are written as text, never coerced. A header that reads as a
 *  number (`2026`) would stop Excel recognising row 1 as a header row, which
 *  breaks exactly the autofilter and Format-as-Table behaviour the A1 rule is
 *  there to protect. */
function writeHeader(sheet: Worksheet, header: TableCell[]): void {
  const row = sheet.getRow(1);
  header.forEach((cell, index) => {
    const target = row.getCell(index + 1);
    target.value = cell.text;
    target.font = HEADER_FONT;
    target.fill = HEADER_FILL;
  });
}

function writeRows(sheet: Worksheet, rows: TableCell[][]): void {
  rows.forEach((cells, rowIndex) => {
    const row = sheet.getRow(rowIndex + 2);
    cells.forEach((cell, columnIndex) => {
      writeValue(row.getCell(columnIndex + 1), coerceCell(cell));
    });
  });
}

/** Rough column widths from the source text, so dates and labels are readable
 *  without the user dragging every border. A too-narrow date column renders as
 *  `########`, which reads as a broken file. */
function fitColumns(sheet: Worksheet, input: XlsxTableInput, width: number): void {
  for (let column = 1; column <= width; column += 1) {
    const longest = [input.header, ...input.rows].reduce(
      (widest, row) => Math.max(widest, row[column - 1]?.text.length ?? 0),
      0,
    );
    sheet.getColumn(column).width = Math.min(
      MAX_COLUMN_WIDTH,
      Math.max(MIN_COLUMN_WIDTH, longest + 2),
    );
  }
}

function writeSourceSheet(sheet: Worksheet, lines: string[]): void {
  lines.forEach((line, index) => {
    sheet.getCell(index + 1, 1).value = line;
  });
  sheet.getColumn(1).width = 80;
}

/** The same text as the `Source` sheet, in the workbook's document properties,
 *  so it survives someone copying the data sheet into another workbook and
 *  leaving `Source` behind. */
function writeProperties(
  workbook: Workbook,
  lines: string[],
  provenance: XlsxTableInput["provenance"],
): void {
  workbook.creator = "Artie";
  workbook.lastModifiedBy = "Artie";
  workbook.created = provenance.timestamp;
  workbook.modified = provenance.timestamp;
  workbook.description = lines.join("\n");

  const company = provenance.company?.trim();
  if (company) workbook.company = company;
}

/** Build the workbook. Async because exceljs is lazy-imported: it is a large
 *  dependency and nobody pays for it until they click Export (mirroring
 *  `ArtifactDocCard.tsx:30`). */
export async function buildTableXlsx(input: XlsxTableInput): Promise<Blob> {
  const excel = await loadExcelJs();

  const lines = buildProvenance({
    ...input.provenance,
    rowCount: input.rows.length,
  });

  const workbook = new excel.Workbook();
  writeProperties(workbook, lines, input.provenance);

  const sheet = workbook.addWorksheet(DATA_SHEET_NAME);
  writeHeader(sheet, input.header);
  writeRows(sheet, input.rows);

  const width = input.rows.reduce(
    (widest, row) => Math.max(widest, row.length),
    input.header.length,
  );
  if (width > 0) {
    // Freeze the header so it stays put on a long table, and put a real
    // autofilter over the block — the affordance a finance user reaches for
    // first, and the reason the header has to sit at A1.
    sheet.views = [{ state: "frozen", ySplit: 1 }];
    sheet.autoFilter = {
      from: { row: 1, column: 1 },
      to: { row: 1 + input.rows.length, column: width },
    };
    fitColumns(sheet, input, width);
  }
  // A zero-column table cannot happen via `extractTable`, which returns null
  // instead. Guarded anyway: an autofilter with an empty range writes a file
  // Excel refuses to open, and a silently corrupt download is this module's
  // worst possible failure.

  writeSourceSheet(workbook.addWorksheet(SOURCE_SHEET_NAME), lines);

  const buffer = await workbook.xlsx.writeBuffer();
  return new Blob([buffer], { type: XLSX_MIME });
}
